<script setup>
import HazardIcon from '@/Components/HazardIcon.vue';
import { trailHrefByName, trailPageHref } from '../hazardLinks.js';
import { computed, inject, ref } from 'vue';
import { styleVars } from './shared.js';
import { hazardDetails, hazardWhen, publicHazardTone } from '@/lib/publicHazard.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});
// With nothing active a visitor gets no section at all (Mus,
// 2026-10-18); the editor canvas keeps it so the manager can find it.
const editing = inject('tiqEditorCanvas', false);

const SEVERITY = {
    high: { label: 'HIGH', tone: 'closed' },
    med: { label: 'MED', tone: 'caution' },
    low: { label: 'LOW', tone: 'low' },
    // Published maintenance hazards carry their own level, coloured as
    // trail signage colours it (inline, off lib/publicHazard.js).
    danger: { label: 'DANGER', level: 'danger' },
    warning: { label: 'WARNING', level: 'warning' },
    caution: { label: 'CAUTION', level: 'caution' },
    notice: { label: 'NOTICE', level: 'notice' },
};

function sevStyle(key) {
    const level = SEVERITY[key]?.level;
    if (!level) return null;
    const t = publicHazardTone(level);
    return { background: t.soft, color: t.ink };
}

// Maintenance items the crew published as hazards, most severe first.
// The crew's description stays in the app; the detail is what kind
// of hazard it is, what an inspection found, whether people can get
// past and the day (hazardDetails).
const publishedRows = computed(() => (props.site_data?.hazards || []).map((h) => ({
    severity: SEVERITY[h.level] ? h.level : 'caution',
    type: h.title,
    trail: h.trail_name || '',
    // The day sits on the right, like a typed hazard's "by".
    detail: hazardDetails(h, { withWhen: false }).join(' · '),
    by: hazardWhen(h),
    href: trailPageHref(h.trail_slug),
    // The row itself, for its type icon. Typed-in hazards have none.
    hazard: h,
})));

// Closed and caution trails carrying a crew note, straight off the
// conditions every other block reads.
const noteRows = computed(() => (props.site_data?.trails || [])
    .filter((t) => (t.bucket === 'closed' || t.bucket === 'caution') && String(t.note ?? '').trim() !== '')
    .map((t) => ({
        severity: t.bucket === 'closed' ? 'high' : 'med',
        type: t.bucket === 'closed' ? 'Closed' : 'Caution',
        trail: t.name,
        detail: String(t.note).trim(),
        by: '',
        href: trailPageHref(t.slug),
    })));

const manualRows = computed(() => (Array.isArray(props.config.items) ? props.config.items : [])
    .filter((h) => h && (String(h.type ?? '').trim() || String(h.trail ?? '').trim() || String(h.detail ?? '').trim()))
    .map((h) => ({
        severity: SEVERITY[h.severity] ? h.severity : 'low',
        type: String(h.type ?? '').trim(),
        trail: String(h.trail ?? '').trim(),
        detail: String(h.detail ?? '').trim(),
        by: String(h.by ?? '').trim(),
        href: trailHrefByName(props.site_data, h.trail),
    })));

const autoRows = computed(() => [...publishedRows.value, ...noteRows.value]);

const rows = computed(() => {
    const src = props.config.source || 'auto';
    if (src === 'manual') return manualRows.value;
    if (src === 'both') return [...manualRows.value, ...autoRows.value];
    return autoRows.value;
});

// Five, then a Load more bar for the rest: a long list of hazards
// pushes everything under it off the page, and the first five are the
// most severe (the list is sorted worst first).
const HAZARDS_FIRST = 5;
const showAll = ref(false);
const shownRows = computed(() => (showAll.value ? rows.value : rows.value.slice(0, HAZARDS_FIRST)));
const hiddenCount = computed(() => (showAll.value ? 0 : Math.max(0, rows.value.length - HAZARDS_FIRST)));

function title(h) {
    return [h.type, h.trail].filter(Boolean).join(' · ');
}
</script>

<template>
    <section v-if="rows.length || editing" :id="config.anchor_id || null" class="th-block th-hazards th-sec" :style="styleVars(config)">
        <div class="th-wrap">
            <h2 v-if="config.heading" class="th-h2 th-h2--sm th-hazards__title">{{ config.heading }}</h2>
            <div v-if="rows.length" class="th-panel th-hazards__list">
                <component :is="h.href ? 'a' : 'div'" v-for="(h, i) in shownRows" :key="i"
                           :href="h.href || undefined" class="th-hazards__row" :class="{ 'th-hazards__row--link': h.href }">
                    <span class="th-tag th-hazards__sev" :class="SEVERITY[h.severity].tone ? `th-hazards__sev--${SEVERITY[h.severity].tone}` : ''" :style="sevStyle(h.severity)">{{ SEVERITY[h.severity].label }}</span>
                    <div class="th-hazards__text">
                        <div v-if="title(h)" class="th-hazards__what"><HazardIcon v-if="h.hazard" :hazard="h.hazard" :size="16" class="th-hazards__icon" />{{ title(h) }}</div>
                        <div v-if="h.detail" class="th-hazards__detail">{{ h.detail }}</div>
                    </div>
                    <span v-if="h.by" class="th-mono th-hazards__by">{{ h.by }}</span>
                </component>
                <button v-if="hiddenCount" type="button" class="th-hazards__more" @click="showAll = true">Load more ({{ hiddenCount }})</button>
            </div>
            <div v-else class="th-card th-hazards__empty">No active hazards right now. Visitors see this section once there is one.</div>
        </div>
    </section>
</template>

<style scoped>
.th-hazards__title { margin-bottom: 16px; }
.th-hazards__list { display: flex; flex-direction: column; }
.th-hazards__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 20px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--th-line-soft);
}
.th-hazards__row:last-child { border-bottom: 0; }
/* The Trails cards' hover, so everything that opens a trail page
   answers the pointer the same way: the edge darkens and lifts. */
.th-hazards__row--link { position: relative; color: inherit; text-decoration: none; transition: box-shadow .15s; }
.th-hazards__row--link:hover { z-index: 1; text-decoration: none; box-shadow: inset 0 0 0 1px var(--th-field), 0 2px 10px rgba(27, 29, 26, .06); }
.th-hazards__row--link:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
/* .th-tag too: `.tpl-trailhead .th-tag` ties this on specificity and
   wins on bundle order, which took the pill's padding. */
.th-tag.th-hazards__sev { min-width: 52px; text-align: center; padding: 5px 9px; }
.th-hazards__sev--closed { background: #f8e2df; color: #9b2a1f; }
.th-hazards__sev--caution { background: #fbf0d9; color: #7d5200; }
.th-hazards__sev--low { background: #e8ecef; color: #3b4a57; }
.th-hazards__text { flex: 1; min-width: 220px; }
.th-hazards__what { font-weight: 600; }
.th-hazards__icon { margin-right: 7px; }
.th-hazards__detail { font-size: 15px; color: var(--th-muted); }
.th-hazards__by { font-size: 11px; text-transform: uppercase; }
.th-hazards__more { border: 0; background: #faf9f6; color: var(--th-ink); padding: 14px 20px; font-family: inherit; font-weight: 600; font-size: 15px; line-height: 1; cursor: pointer; text-align: center; }
.th-hazards__more:hover { background: #f1efe9; }
.th-hazards__more:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.th-hazards__empty { color: var(--th-muted); }
</style>
